import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from '@/services/auth'
import { STATUS } from '@/utils/constants'
import toast from 'react-hot-toast'
import { getUserFriendlyErrorMessage } from '@/utils/errorMessages'

// Initial state - start with loading to prevent redirect during auth check
const initialState = {
  user: null,
  isAuthenticated: false,
  status: STATUS.LOADING,
  error: null,
}

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
}

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        status: STATUS.LOADING,
        error: null,
      }

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        status: STATUS.SUCCESS,
        error: null,
      }

    case AUTH_ACTIONS.LOGIN_ERROR:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        status: STATUS.ERROR,
        error: action.payload.error,
      }

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        status: STATUS.IDLE,
        error: null,
      }

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload.user,
        status: STATUS.SUCCESS,
      }

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        status: STATUS.ERROR,
        error: action.payload.error,
      }

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        status: state.isAuthenticated ? STATUS.SUCCESS : STATUS.IDLE,
      }

    default:
      return state
  }
}

// Create context
const AuthContext = createContext()

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Set loading state during initialization
      dispatch({ type: AUTH_ACTIONS.SET_LOADING })

      const token = authService.getStoredToken()
      const storedUser = authService.getStoredUser()

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          const user = await authService.getCurrentUser()
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user },
          })
        } catch (error) {
          // Token is invalid, clear stored data
          authService.logout()
          dispatch({ type: AUTH_ACTIONS.LOGOUT })
        }
      } else {
        // No token/user found, set to idle state (not authenticated)
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      }
    }

    initializeAuth()
  }, [])

  // Listen for storage changes (token removal from other tabs or API interceptor)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'sr_internal_token' && !e.newValue && state.isAuthenticated) {
        // Token was removed, logout user
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      }
    }

    // Check token periodically for same-tab changes (less aggressive - every 30 seconds)
    const checkTokenInterval = setInterval(() => {
      const token = authService.getStoredToken()
      if (!token && state.isAuthenticated) {
        // Token was removed, logout user
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      } else if (token && state.isAuthenticated) {
        // Check if token is expired (not near expiration, actually expired)
        if (authService.isTokenExpired(token)) {
          dispatch({ type: AUTH_ACTIONS.LOGOUT })
        }
      }
    }, 30000) // Check every 30 seconds instead of every second

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(checkTokenInterval)
    }
  }, [state.isAuthenticated])

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING })
      
      const { user } = await authService.login(credentials)
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user },
      })
      
      toast.success('Welcome back!')
      return { success: true }
    } catch (error) {
      const errorMessage = getUserFriendlyErrorMessage(error, 'The email or password you entered is incorrect. Please try again.')

      dispatch({
        type: AUTH_ACTIONS.LOGIN_ERROR,
        payload: { error: errorMessage },
      })

      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING })
      
      await authService.register(userData)
      
      toast.success('Registration successful! Please check your email to verify your account.')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed'
      
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: { error: errorMessage },
      })
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Logout function
  const logout = () => {
    authService.logout()
    dispatch({ type: AUTH_ACTIONS.LOGOUT })
    toast.success('You have been logged out successfully.')
  }

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING })

      const user = await authService.updateProfile(userData)

      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: { user },
      })

      toast.success('Profile updated successfully!')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Profile update failed'

      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: { error: errorMessage },
      })

      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Update user data (for direct updates)
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: { user: userData },
    })
  }

  // Change password
  const changePassword = async (passwordData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING })
      
      await authService.changePassword(passwordData)
      
      toast.success('Password changed successfully!')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password change failed'
      
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: { error: errorMessage },
      })
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    updateUser,
    changePassword,
    clearError,
    // Helper functions
    hasPermission: (permission) => authService.hasPermission(state.user, permission),
    hasRole: (role) => authService.hasRole(state.user, role),
    canAccessBrand: (brandId) => authService.canAccessBrand(state.user, brandId),
    hasBrandPermission: (brandId, permission) => authService.hasBrandPermission(state.user, brandId, permission),
    getAccessibleBrandIds: () => authService.getAccessibleBrandIds(state.user),
    canManageDataInput: (dataInput, action) => authService.canManageDataInput(state.user, dataInput, action),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
